import rateTypeNames from 'lib/rate-type-names';
import { isValidNumber } from 'lib/is-valid-number';
import { isZeroDecimalCurrency } from 'lib/currencies';
import { DRAFT_JS_EMPTY_DESCRIPTION } from 'lib/draft-js-empty-description';
import moment from 'moment';

import { ERRORS_OBJ } from './errorsObj';

export const modelToValidatorAction = {
  title: title => (!hasText(title) ? [ERRORS_OBJ.listing.NO_TITLE()] : []),
  address: (address, countryCode, whitelistedCountries) => validateAddress(address, countryCode, whitelistedCountries),
  virtualInfo: (virtualInfo, virtual) => validateVirtualInfo(virtualInfo, virtual),
  description: description =>
    description === DRAFT_JS_EMPTY_DESCRIPTION || !hasText(description) ? [ERRORS_OBJ.listing.NO_DESCRIPTION()] : [],
  capacity: (capacity) => validateCapacity(capacity),
  rates: (rates, srcCurrency, eventCapacity, urgencyIndicatorEnabled) =>
    validateRates(rates, srcCurrency, eventCapacity, urgencyIndicatorEnabled),
  timeSlots: timeSlots => validateTimeSlots(timeSlots),
};

function validateCapacity(capacity) {
  // empty string is valid, for setting back to null
  const errors = [];
  if (capacity && capacity !== '' && !isValidNumber(capacity, false)) {
    errors.push(ERRORS_OBJ.listing.INVALID_CAPACITY());
  };
  return errors;
}

function validateAddress(address, countryCode, whitelistedCountries = []) {
  const errors = [];

  if (!address) errors.push(ERRORS_OBJ.listing.NO_ADDRESS());
  if (!whitelistedCountries.map(c => c.code).includes(countryCode))
    errors.push(ERRORS_OBJ.listing.BLACKLISTED_COUNTRY());

  return errors;
}

function validateVirtualInfo(virtualInfo, virtual) {
  const errors = [];
  if (virtual && !hasText(virtualInfo)) errors.push(ERRORS_OBJ.listing.NO_VIRTUAL_INFO());
  return errors;
}

function validateRates(rates, srcCurrency, eventCapacity, urgencyIndicatorEnabled) {
  const errors = [];
  let checkForDistinctTitles = true;

  if (!rates || !rates.length) errors.push(ERRORS_OBJ.listing.NO_RATES());

  const startDate = sd => sd && sd._isValid; // eslint-disable-line no-underscore-dangle
  const endDate = ed => ed && ed._isValid; // eslint-disable-line no-underscore-dangle
  const startTime = st => Boolean(st);
  const endTime = et => Boolean(et);
  const startDateBeforeEndDate = (sd, ed, st, et) => {
    if (ed.unix() > sd.unix()) return true;
    else if (ed.unix() === sd.unix() && moment(et.title, 'hh:mm a').isAfter(moment(st.title, 'hh:mm a'))) return true;
    return false;
  };

  const startDateTimeExist = rate => startDate(rate.startDate) && startTime(rate.startTime);
  const endDateTimeExist = rate => endDate(rate.endDate) && endTime(rate.endTime);
  const validStartDate = rate => (startTime(rate.startTime) ? Boolean(startDate(rate.startDate)) : true);
  const validStartTime = rate => (startDate(rate.startDate) ? Boolean(startTime(rate.startTime)) : true);
  const validEndDate = rate => (endTime(rate.endTime) ? Boolean(endDate(rate.endDate)) : true);
  const validEndTime = rate => (endDate(rate.endDate) ? Boolean(endTime(rate.endTime)) : true);
  const validMinMaxQuantities = rate => {
    if (
      (rate.minQuantity && !isValidNumber(rate.minQuantity, false)) ||
      (rate.maxQuantity && !isValidNumber(rate.maxQuantity, false))
    )
      return false;
    return rate.minQuantity && rate.maxQuantity
      ? parseInt(rate.maxQuantity, 10) >= parseInt(rate.minQuantity, 10)
      : true;
  };
  const validMaxRedemptions = rate => {
    return !rate.maxRedemptions || isValidNumber(rate.maxRedemptions, false);
  };
  const validRedemptionMessage = rate => {
    return !rate.redemptionMessageActive || (rate.redemptionMessageActive && rate.redemptionMessage);
  };

  rates.forEach(rate => {
    if (!rate.name || rate.name.length > 255) {
      checkForDistinctTitles = false;
      errors.push(ERRORS_OBJ.rates.NO_NAME(rate.key));
    }

    if (rate.type === rateTypeNames.paid && !rate.price) errors.push(ERRORS_OBJ.rates.NO_PRICE(rate.key));

    if (
      (rate.type === rateTypeNames.paid || (rate.type === rateTypeNames.pwyw && rate.minPrice)) &&
      !isValidNumber(
        rate.type === rateTypeNames.paid ? rate.price : rate.minPrice,
        rate.type === rateTypeNames.pwyw,
        isZeroDecimalCurrency(srcCurrency) ? 0 : 2,
      )
    )
      errors.push(ERRORS_OBJ.rates.INVALID_PRICE(rate.key));

    if (!validStartDate(rate)) errors.push(ERRORS_OBJ.rates.NO_START_DATE(rate.key));
    if (!validStartTime(rate)) errors.push(ERRORS_OBJ.rates.NO_START_TIME(rate.key));

    if (!validEndDate(rate)) errors.push(ERRORS_OBJ.rates.NO_END_DATE(rate.key));
    if (!validEndTime(rate)) errors.push(ERRORS_OBJ.rates.NO_END_TIME(rate.key));

    if (rate.capacity && !isValidNumber(rate.capacity)) errors.push(ERRORS_OBJ.rates.INVALID_CAPACITY(rate.key));

    if (urgencyIndicatorEnabled && !eventCapacity) {
      errors.push(ERRORS_OBJ.listing.CAPACITY_REQUIRED());
    }

    if (
      startDateTimeExist(rate) &&
      endDateTimeExist(rate) &&
      !startDateBeforeEndDate(rate.startDate.startOf('day'), rate.endDate.startOf('day'), rate.startTime, rate.endTime)
    )
      errors.push(ERRORS_OBJ.rates.END_DATE_BEFORE_START_DATE(rate.key));

    if (!validMinMaxQuantities(rate)) errors.push(ERRORS_OBJ.rates.MAX_QUANTITY_LOWER_THAN_MIN(rate.key));

    if (!validMaxRedemptions(rate)) errors.push(ERRORS_OBJ.rates.INVALID_MAX_REDEMPTIONS(rate.key))
    if (!validRedemptionMessage(rate)) errors.push(ERRORS_OBJ.rates.NO_REDEMPTION_MESSAGE(rate.key));
  });

  if (checkForDistinctTitles) {
    const distinct = [...new Set(rates.map(r => r.name))];
    if (distinct.length !== rates.length) errors.push(ERRORS_OBJ.listing.DUPLICATE_RATE_NAMES());
  }

  return errors;
}

function validateTimeSlots(timeSlots) {
  const errors = [];

  //  prevent the exact same timeslot signature from being entered
  const datetimeToTimeSlotIds = timeSlots.reduce((acc, ts) => {
    const nextObj = acc;
    const signature = ts.startAt.concat('-', ts.endAt);
    const signatureExists = !!nextObj[signature];

    const signatureExistsToAction = {
      true: timeslotId => nextObj[signature].push(timeslotId),
      false: timeslotId => {
        nextObj[signature] = [timeslotId];
      },
    };
    signatureExistsToAction[signatureExists](ts.id);

    return nextObj;
  }, {});

  function endAtBeforeStartAt(startAt, endAt) {
    if (startAt.unix() > endAt.unix()) return true;
    return false;
  }

  // relates to note below - please keep this method here
  // function startAtIsBeforeRightNow(startAt) {
  //   return startAt < moment();
  // }

  timeSlots.forEach(date => {
    const { id, startAt, endAt } = date;
    const signature = startAt.concat('-', endAt);

    if (datetimeToTimeSlotIds[signature].length > 1) errors.push(ERRORS_OBJ.timeSlots.OVERLAPPING_DATETIME(id));

    if (startAt.length === 0)
      errors.push(ERRORS_OBJ.timeSlots.NO_START_DATE(id), ERRORS_OBJ.timeSlots.NO_START_TIME(id));

    if (endAtBeforeStartAt(moment(startAt), moment(endAt)))
      errors.push(ERRORS_OBJ.timeSlots.END_DATE_BEFORE_START_DATE(id));

    // keeping this validation temporarily disabled for now - need to add functionality for
    //  greying out + disabling a timeslot onLoad if its date has already passed the current date
    //  also add an info tooltip to show the "1+ person has already booked this event ..." message
    // if (startAtIsBeforeRightNow(moment(startAt))) errors.push(ERRORS_OBJ.timeSlots.DATE_IS_BEFORE_CURRENT_TIME(id));
  });

  return errors;
}

// helpers
const hasText = text => text && text.replace(/ /g, '').length > 0;
